import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { BookOpen, ArrowRight, ArrowLeft, Upload, X, AlertCircle, Loader2, FileText, FileSpreadsheet, FileType, File, Paperclip } from 'lucide-react';
import { useAgent } from '../../context/AgentContext';
import Button from '../../components/Button';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

export default function StepKnowledge() {
  const { goNext, goPrev } = useOutletContext();
  const {
    knowledgeFiles,
    uploadKnowledgeFile,
    deleteKnowledgeFile,
    loadKnowledgeFiles,
    isLoading,
    error,
    clearError
  } = useAgent();

  const [isDragging, setIsDragging] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [deletingFileId, setDeletingFileId] = useState(null);

  // Load existing files on mount
  useEffect(() => {
    loadKnowledgeFiles();
  }, [loadKnowledgeFiles]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
    e.target.value = ''; // Reset input
  };

  const addFiles = async (newFiles) => {
    const validFiles = [];
    const rejected = [];

    newFiles.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        rejected.push({
          name: file.name,
          size: file.size,
          reason: "File exceeds 10MB limit",
        });
      } else {
        validFiles.push(file);
      }
    });

    // Show rejected files
    if (rejected.length > 0) {
      setRejectedFiles(rejected);
      setTimeout(() => setRejectedFiles([]), 5000);
    }

    // Upload valid files
    for (const file of validFiles) {
      const tempId = `uploading-${Date.now()}-${file.name}`;
      setUploadingFiles(prev => [...prev, { id: tempId, name: file.name }]);

      try {
        await uploadKnowledgeFile(file);
      } catch (err) {
        console.error('Failed to upload file:', err);
      } finally {
        setUploadingFiles(prev => prev.filter(f => f.id !== tempId));
      }
    }
  };

  const dismissRejectedFiles = () => {
    setRejectedFiles([]);
  };

  const handleRemoveFile = async (fileId) => {
    setDeletingFileId(fileId);
    try {
      await deleteKnowledgeFile(fileId);
    } catch (err) {
      console.error('Failed to delete file:', err);
    } finally {
      setDeletingFileId(null);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return FileText;
    if (['doc', 'docx'].includes(ext)) return FileType;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
    if (['txt'].includes(ext)) return File;
    return Paperclip;
  };

  const allFiles = [...knowledgeFiles, ...uploadingFiles.map(f => ({ ...f, uploading: true }))];

  return (
    <div className="p-8 max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Knowledge Base</h1>
            <p className="text-text-secondary">Upload documents to train your AI</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
          <button onClick={clearError} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          card p-8 border-2 border-dashed text-center cursor-pointer
          transition-all duration-200
          ${isDragging
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/50 hover:bg-gray-50'
          }
          ${isLoading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
          disabled={isLoading}
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <div className="flex flex-col items-center">
            <div className={`
              p-4 rounded-2xl mb-4 transition-colors
              ${isDragging ? 'bg-primary/20' : 'bg-primary/10'}
            `}>
              <Upload className={`w-8 h-8 ${isDragging ? 'text-primary' : 'text-primary/60'}`} />
            </div>
            <p className="font-medium text-text-primary mb-1">
              {isDragging ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-sm text-text-muted mb-4">
              or click to browse
            </p>
            <p className="text-xs text-text-muted">
              Supports PDF, DOC, DOCX, TXT, CSV, XLSX (Max 10MB each)
            </p>
          </div>
        </label>
      </div>

      {/* Rejected Files Warning */}
      {rejectedFiles.length > 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl animate-slide-up">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">
                  {rejectedFiles.length} file{rejectedFiles.length > 1 ? "s" : ""} couldn't be uploaded
                </p>
                <p className="text-sm text-red-600 mt-1">
                  Files must be 10MB or less.
                </p>
                <ul className="mt-2 space-y-1">
                  {rejectedFiles.map((file, index) => (
                    <li key={index} className="text-sm text-red-700">
                      {file.name} ({formatBytes(file.size)})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <button
              onClick={dismissRejectedFiles}
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* File List */}
      {allFiles.length > 0 && (
        <div className="mt-6 space-y-3">
          <h3 className="text-sm font-medium text-text-primary">
            Uploaded Files ({knowledgeFiles.length})
          </h3>
          {allFiles.map((file) => {
            const FileIcon = getFileIcon(file.original_name || file.name);
            return (
            <div
              key={file.id}
              className={`
                flex items-center gap-3 p-4 bg-white rounded-xl border border-border group animate-slide-up
                ${file.uploading ? 'opacity-60' : ''}
              `}
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 flex-shrink-0">
                <FileIcon className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-text-primary truncate">
                  {file.original_name || file.name}
                </p>
                <p className="text-sm text-text-muted">
                  {file.uploading ? 'Uploading...' : formatBytes(file.size || 0)}
                </p>
              </div>
              {file.uploading ? (
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              ) : deletingFileId === file.id ? (
                <Loader2 className="w-5 h-5 text-red-500 animate-spin" />
              ) : (
                <button
                  onClick={() => handleRemoveFile(file.id)}
                  disabled={isLoading || deletingFileId !== null}
                  className="p-2 text-text-muted hover:text-red-500 hover:bg-red-50 rounded-lg
                             opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Info */}
      <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100">
        <h4 className="text-sm font-medium text-amber-800 mb-1">Optional Step</h4>
        <p className="text-sm text-amber-700">
          You can skip this step and add documents later. Your AI will use the business description to answer questions.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={goPrev}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          icon={ArrowRight}
          iconPosition="right"
          onClick={goNext}
          disabled={uploadingFiles.length > 0}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}